/**
 * AddProductModal Component
 * Modal dialog for adding new products (geogrid or drainage)
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    OutlinedInput,
    SelectChangeEvent,
    Alert,
} from '@mui/material';
import { IAdminGeogridProductRequest, IAdminDrainageProductRequest, ProductType, APPLICABLE_PROJECT_TYPES, DRAINAGE_CATEGORIES, APPLICABLE_LAYERS } from '../types/AdminTypes';

interface AddProductModalProps {
    open: boolean;
    onClose: () => void;
    onAddGeogrid: (product: IAdminGeogridProductRequest) => Promise<void>;
    onAddDrainage: (product: IAdminDrainageProductRequest) => Promise<void>;
    productType: ProductType;
    families: string[];
    loading?: boolean;
}

const initialGeogridProduct: IAdminGeogridProductRequest = {
    prodotto: '',
    family: '',
    familyId: '',
    category: 'GEOGRID',
    tensile1Percent: null,
    tensile2Percent: null,
    tensile3Percent: null,
    tensile4Percent: null,
    tensile5Percent: null,
    torsionalStiffness: null,
    lcrEquationM2: null,
    lcrEquationM1: null,
    lcrEquationC: null,
    lcrEquationThreshold: null,
    applicableProjects: [],
    applicableRegions: null,
};

const initialDrainageProduct: IAdminDrainageProductRequest = {
    displayName: '',
    category: 'macdrainW',
    effectivePorosity: null,
    permeabilityCoefficient: null,
    transmissivity: null,
    thickness: null,
    rfCreep: null,
    description: '',
    databaseReference: null,
    notes: null,
    applicableLayersOnly: [],
};

export const AddProductModal: React.FC<AddProductModalProps> = ({ open, onClose, onAddGeogrid, onAddDrainage, productType, families, loading }) => {
    const [geogridProduct, setGeogridProduct] = useState<IAdminGeogridProductRequest>(initialGeogridProduct);
    const [drainageProduct, setDrainageProduct] = useState<IAdminDrainageProductRequest>(initialDrainageProduct);
    const [error, setError] = useState<string | null>(null);

    const handleClose = () => {
        setGeogridProduct(initialGeogridProduct);
        setDrainageProduct(initialDrainageProduct);
        setError(null);
        onClose();
    };

    const handleGeogridChange = (field: keyof IAdminGeogridProductRequest, value: string | number | null | string[]) => {
        setGeogridProduct(prev => ({ ...prev, [field]: value }));

        // Auto-generate familyId from family name
        if (field === 'family' && typeof value === 'string') {
            const familyId = value
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');
            setGeogridProduct(prev => ({ ...prev, familyId }));
        }
    };

    const handleDrainageChange = (field: keyof IAdminDrainageProductRequest, value: string | number | null | string[]) => {
        setDrainageProduct(prev => ({ ...prev, [field]: value }));
    };

    const handleProjectTypesChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        handleGeogridChange('applicableProjects', typeof value === 'string' ? value.split(',') : value);
    };

    const validateGeogridProduct = (): boolean => {
        if (!geogridProduct.prodotto.trim()) {
            setError('Product name is required');
            return false;
        }
        if (!geogridProduct.family.trim()) {
            setError('Family is required');
            return false;
        }
        // LCR fields must be all filled or all empty
        const lcrFields = [geogridProduct.lcrEquationM2, geogridProduct.lcrEquationM1, geogridProduct.lcrEquationC, geogridProduct.lcrEquationThreshold];
        const filledLcrCount = lcrFields.filter(v => v != null).length;
        if (filledLcrCount > 0 && filledLcrCount < 4) {
            setError('LCR equation requires all 4 fields (M2, M1, C, Threshold) or none.');
            return false;
        }
        return true;
    };

    const validateDrainageProduct = (): boolean => {
        if (!drainageProduct.displayName.trim()) {
            setError('Display name is required');
            return false;
        }
        if (!drainageProduct.category.trim()) {
            setError('Category is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        setError(null);

        try {
            if (productType === 'geogrid') {
                if (!validateGeogridProduct()) return;
                await onAddGeogrid(geogridProduct);
            } else {
                if (!validateDrainageProduct()) return;
                await onAddDrainage(drainageProduct);
            }
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add product');
        }
    };

    const renderGeogridForm = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField fullWidth required label="Product Name" value={geogridProduct.prodotto} onChange={e => handleGeogridChange('prodotto', e.target.value)} placeholder="e.g., MACGRID EG 50S" />
            </Grid>
            <Grid item xs={8}>
                <FormControl fullWidth required>
                    <InputLabel>Family</InputLabel>
                    <Select value={geogridProduct.family} label="Family" onChange={e => handleGeogridChange('family', e.target.value)}>
                        {families.map(family => (
                            <MenuItem key={family} value={family}>
                                {family}
                            </MenuItem>
                        ))}
                        <MenuItem value="">
                            <em>Enter new family below...</em>
                        </MenuItem>
                    </Select>
                </FormControl>
                {!geogridProduct.family && (
                    <TextField
                        fullWidth
                        size="small"
                        label="New Family Name"
                        value={geogridProduct.family}
                        onChange={e => handleGeogridChange('family', e.target.value)}
                        placeholder="e.g., MacGrid EG"
                        sx={{ mt: 1 }}
                    />
                )}
            </Grid>
            <Grid item xs={4}>
                <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select value={geogridProduct.category} label="Category" onChange={e => handleGeogridChange('category', e.target.value)}>
                        <MenuItem value="GEOGRID">GEOGRID</MenuItem>
                        <MenuItem value="GEOTEXTILE">GEOTEXTILE</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Tensile Properties
                </Typography>
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Tensile 1%"
                    value={geogridProduct.tensile1Percent ?? ''}
                    onChange={e => handleGeogridChange('tensile1Percent', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Tensile 2%"
                    value={geogridProduct.tensile2Percent ?? ''}
                    onChange={e => handleGeogridChange('tensile2Percent', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Tensile 3%"
                    value={geogridProduct.tensile3Percent ?? ''}
                    onChange={e => handleGeogridChange('tensile3Percent', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Tensile 4%"
                    value={geogridProduct.tensile4Percent ?? ''}
                    onChange={e => handleGeogridChange('tensile4Percent', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Tensile 5%"
                    value={geogridProduct.tensile5Percent ?? ''}
                    onChange={e => handleGeogridChange('tensile5Percent', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    type="number"
                    label="Torsional Stiffness"
                    value={geogridProduct.torsionalStiffness ?? ''}
                    onChange={e => handleGeogridChange('torsionalStiffness', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.01, min: 0 }}
                />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    LCR Equation Coefficients
                </Typography>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    fullWidth
                    type="number"
                    label="LCR M2"
                    value={geogridProduct.lcrEquationM2 ?? ''}
                    onChange={e => handleGeogridChange('lcrEquationM2', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.0000001, min: 0 }}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    fullWidth
                    type="number"
                    label="LCR M1"
                    value={geogridProduct.lcrEquationM1 ?? ''}
                    onChange={e => handleGeogridChange('lcrEquationM1', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.000001 }}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    fullWidth
                    type="number"
                    label="LCR C"
                    value={geogridProduct.lcrEquationC ?? ''}
                    onChange={e => handleGeogridChange('lcrEquationC', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.0001, min: 0 }}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    fullWidth
                    type="number"
                    label="LCR Threshold"
                    value={geogridProduct.lcrEquationThreshold ?? ''}
                    onChange={e => handleGeogridChange('lcrEquationThreshold', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 1, min: 0 }}
                />
            </Grid>

            <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="add-product-projects-label">Applicable Project Types</InputLabel>
                    <Select
                        labelId="add-product-projects-label"
                        id="add-product-projects-select"
                        multiple
                        value={geogridProduct.applicableProjects || []}
                        onChange={handleProjectTypesChange}
                        input={<OutlinedInput label="Applicable Project Types" />}
                        renderValue={selected => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map(value => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                            </Box>
                        )}>
                        {APPLICABLE_PROJECT_TYPES.map(project => (
                            <MenuItem key={project} value={project}>
                                {project.replace('_', ' ').toUpperCase()}
                            </MenuItem>
                        ))}
                    </Select>
                    {(geogridProduct.applicableProjects ?? []).length === 0 && (
                        <FormHelperText sx={{ color: 'warning.main' }}>May be hidden from project-type filtered pickers when empty.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        </Grid>
    );

    const renderDrainageForm = () => (
        <Grid container spacing={2}>
            <Grid item xs={8}>
                <TextField
                    fullWidth
                    required
                    label="Display Name"
                    value={drainageProduct.displayName}
                    onChange={e => handleDrainageChange('displayName', e.target.value)}
                    placeholder="e.g., MacDrain W 1041"
                />
            </Grid>
            <Grid item xs={4}>
                <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select value={drainageProduct.category} label="Category" onChange={e => handleDrainageChange('category', e.target.value)}>
                        {DRAINAGE_CATEGORIES.map(cat => (
                            <MenuItem key={cat} value={cat}>
                                {cat}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Physical Properties
                </Typography>
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Effective Porosity (0-1)"
                    value={drainageProduct.effectivePorosity ?? ''}
                    onChange={e => handleDrainageChange('effectivePorosity', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.01, min: 0, max: 1 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Permeability (m/day)"
                    value={drainageProduct.permeabilityCoefficient ?? ''}
                    onChange={e => handleDrainageChange('permeabilityCoefficient', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.1, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Transmissivity"
                    value={drainageProduct.transmissivity ?? ''}
                    onChange={e => handleDrainageChange('transmissivity', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.01, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="Thickness (m)"
                    value={drainageProduct.thickness ?? ''}
                    onChange={e => handleDrainageChange('thickness', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.001, min: 0 }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    type="number"
                    label="RF Creep"
                    value={drainageProduct.rfCreep ?? ''}
                    onChange={e => handleDrainageChange('rfCreep', e.target.value ? Number(e.target.value) : null)}
                    inputProps={{ step: 0.01, min: 0 }}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Description" value={drainageProduct.description ?? ''} onChange={e => handleDrainageChange('description', e.target.value || null)} />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Notes" value={drainageProduct.notes ?? ''} onChange={e => handleDrainageChange('notes', e.target.value || null)} />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="add-product-layers-label">Applicable Layers Only</InputLabel>
                    <Select
                        labelId="add-product-layers-label"
                        id="add-product-layers-select"
                        multiple
                        value={drainageProduct.applicableLayersOnly ?? []}
                        onChange={(event: SelectChangeEvent<string[]>) => {
                            const { value } = event.target;
                            const nextLayers = typeof value === 'string' ? value.split(',') : value;
                            handleDrainageChange('applicableLayersOnly', nextLayers);
                        }}
                        input={<OutlinedInput label="Applicable Layers Only" />}
                        renderValue={selected => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map(value => (
                                    <Chip key={value} label={value.toUpperCase()} size="small" />
                                ))}
                            </Box>
                        )}>
                        {APPLICABLE_LAYERS.map(layer => (
                            <MenuItem key={layer} value={layer}>
                                {layer.toUpperCase()}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                // Override project-wide compact-input rules (style/mui.scss + ProjectInfo.scss)
                // that force MuiOutlinedInput-root to 30px and break floating-label vertical centering.
                '& .MuiOutlinedInput-root': { height: 'auto !important' },
            }}>

            <DialogTitle>Add New {productType === 'geogrid' ? 'Geogrid' : 'Drainage'} Product</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {productType === 'geogrid' ? renderGeogridForm() : renderDrainageForm()}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? 'Adding...' : 'Add Product'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
